const ONE_HOUR_MS = 60 * 60 * 1000;
const SEGMENT_KEYS = ['months', 'days', 'hours', 'minutes'] as const;

type SegmentKey = (typeof SEGMENT_KEYS)[number];

type CountdownParts = Record<SegmentKey, number>;

const ZERO_PARTS: CountdownParts = {
  months: 0,
  days: 0,
  hours: 0,
  minutes: 0
};

function pad2(value: number): string {
  return String(Math.max(0, value)).padStart(2, '0');
}

/**
 * Calendar-aware remaining time from `now` until `event`.
 * Months are whole calendar months; the remainder is then split into
 * days / hours / minutes. Never returns negative values.
 */
function addCalendarMonths(date: Date, months: number): Date {
  const result = new Date(date);
  const dayOfMonth = result.getDate();

  // Move through the first of the month so dates such as January 31 do not
  // overflow into March before the target month can be clamped.
  result.setDate(1);
  result.setMonth(result.getMonth() + months);

  const lastDayOfTargetMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(dayOfMonth, lastDayOfTargetMonth));

  return result;
}

export function diffParts(now: Date, event: Date): CountdownParts {
  if (event.getTime() <= now.getTime()) {
    return { ...ZERO_PARTS };
  }

  let months = (event.getFullYear() - now.getFullYear()) * 12 + (event.getMonth() - now.getMonth());

  let anchor = addCalendarMonths(now, months);

  if (anchor.getTime() > event.getTime()) {
    months -= 1;
    anchor = addCalendarMonths(now, months);
  }

  let remainingMs = event.getTime() - anchor.getTime();

  const days = Math.floor(remainingMs / (24 * 60 * 60 * 1000));
  remainingMs -= days * 24 * 60 * 60 * 1000;

  const hours = Math.floor(remainingMs / (60 * 60 * 1000));
  remainingMs -= hours * 60 * 60 * 1000;

  const minutes = Math.floor(remainingMs / (60 * 1000));

  return {
    months: Math.max(0, months),
    days,
    hours,
    minutes
  };
}

function setPastState(
  segments: HTMLElement | null,
  pastMessage: HTMLElement | null,
  isPast: boolean
): void {
  if (segments) {
    segments.toggleAttribute('hidden', isPast);
  }
  if (pastMessage) {
    pastMessage.toggleAttribute('hidden', !isPast);
  }
}

function writeValues(root: HTMLElement, parts: CountdownParts): void {
  for (const key of SEGMENT_KEYS) {
    const el = root.querySelector<HTMLElement>(`[data-countdown-value="${key}"]`);
    if (el) {
      el.textContent = pad2(parts[key]);
    }
  }
}

export default function countdownTimer(root: HTMLElement): void {
  const raw = root.dataset.eventDatetime;
  if (!raw) return;

  const event = new Date(raw);
  if (Number.isNaN(event.getTime())) return;

  const segments = root.querySelector<HTMLElement>('[data-countdown-segments]');
  const pastMessage = root.querySelector<HTMLElement>('[data-countdown-past]');
  let intervalId: number | undefined;

  const tick = (): boolean => {
    const now = new Date();
    const delta = now.getTime() - event.getTime();

    if (delta > ONE_HOUR_MS) {
      setPastState(segments, pastMessage, true);
      writeValues(root, ZERO_PARTS);
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
      return false;
    }

    setPastState(segments, pastMessage, false);

    if (delta >= 0) {
      // Event started; hold zeros for the remainder of the one-hour window.
      writeValues(root, ZERO_PARTS);
      return true;
    }

    writeValues(root, diffParts(now, event));
    return true;
  };

  if (tick()) {
    intervalId = window.setInterval(tick, 1000);
  }
}
